import { useCallback } from "react";
import { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { isImageFile } from "@/other/utils/utils.jsx";
import {
  buildSpotIncentivePrefill,
  prepareFileForSpotIncentive,
} from "@/react/components/entityViews/files/utils/spotIncentiveFileUtils";
import { toast } from "sonner";
import type { ISwivuProject } from "@/types/SwivuProject";
import type { EntityRecord } from "@/types/entity";
import { openAdjustPointsEditorWithGuard } from "@/other/utils/incentiveUtils.jsx";

interface UseFileSpotIncentiveProps {
  entityRecord: EntityRecord;
  project: ISwivuProject | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorHandler: any;
  onSuccess?: () => void;
}

/**
 * Custom hook to handle spot incentive operations for files
 * Provides logic for checking if a file is eligible for spot incentive
 * and handler for opening the spot incentive editor
 */
export const useFileSpotIncentive = ({
  entityRecord,
  project,
  editorHandler,
  onSuccess
}: UseFileSpotIncentiveProps) => {
  /**
   * Check if "Adjust Points" should be shown for a file
   * Both manual and CompanyCam images are eligible
   */
  const shouldShowAdjustPoints = useCallback(
    (file: SwivuFile | null) => {
      if (!file) return false;

      const isImage = isImageFile(file);
      const isTaskRelated = file.task !== null;

      return isImage && isTaskRelated;
    },
    []
  );

  /**
   * Handler for adjusting points on a file
   */
  const handleAdjustPoints = useCallback(
    async (
      file: SwivuFile,
      isCreatingUnreleasedRecord?: boolean,
      onReviewClick?: (newFile?: SwivuFile) => void,
      extras?: { onFileDetailResume?: () => void },
    ) => {
      if (!file || !project) return;

      try {
        const fileData = await prepareFileForSpotIncentive(file);

        if (!fileData) {
          toast.error("Unable to use this file for spot point");
          return;
        }

        const task = file.task != null ? project.extraTaskStore.getById(file.task) : undefined;
        const prefill = buildSpotIncentivePrefill({
          file,
          fileData,
          task,
          project,
          entityRecord,
        });

        openAdjustPointsEditorWithGuard({
          project,
          editorHandler,
          record: task,
          otherParams: {
            initialFileId: prefill.initialFileId,
            initialFileData: prefill.initialFileData,
            isCreatingUnreleasedRecord,
            onReviewClick,
            onFileDetailResume: extras?.onFileDetailResume,
            initialName: prefill.initialName,
            initialStartDate: prefill.initialStartDate,
            initialEndDate: prefill.initialEndDate,
            initialResourceIds: prefill.initialResourceIds,
          }
        });

        onSuccess?.();
      } catch (error) {
        console.error("Error creating spot point:", error);
        toast.error("Failed to create spot point");
      }
    },
    [project, editorHandler, entityRecord, onSuccess]
  );

  return {
    shouldShowAdjustPoints,
    handleAdjustPoints
  };
};
